import { KnownSettingsNodeDtoType, KnownSettingsNodeDtoStatus } from '@groundfloor-platform/mgmt-service-g-sdk';
import {
  EnrichedSettingsNodeDto,
  EnrichedSettingsTreeDto,
  SettingsNodeStyle,
  SettingsTreeDataRequestType
} from '../reducer/settings-tree-reducer.types';

export interface ISettingsNode {
  label: string;
  id: string;
  parentId: string | null; // Optional property to link to the parent node. null if it's a root node.
  type: KnownSettingsNodeDtoType;
  subType?: string;
  isPlaceholder?: boolean;
  children: ISettingsNode[];
  isHidden?: boolean; // Optional property to hide nodes in the UI
  isExpandableWhenEmpty?: boolean; // Optional property to control expandability when there are no children
  status?: KnownSettingsNodeDtoStatus;
  style?: SettingsNodeStyle;
  requestType: SettingsTreeDataRequestType;
}

export interface ISettingsTree {
  root?: ISettingsNode;
}

// The SettingsTreeBuilderCache is used to count the number of times a node has been processed.
type SettingsTreeBuilderCache = {
  nodesVisited: Set<string>;
};

/**
 * Builds a tree structure from the flat list of nodes and relationships returned by the API.
 * @param projectTree The flat list of nodes and relationships returned by the API.
 * @returns The tree structure.
 */
export function buildSettingsTree(
  projectTree: EnrichedSettingsTreeDto,
  projectChildrenTree: EnrichedSettingsTreeDto,
  placeholderNodes: EnrichedSettingsNodeDto[],
  availableUiFeatures: string[]
): ISettingsTree {
  const root = projectTree.nodes.find((node) => node.type === KnownSettingsNodeDtoType.Root);
  if (!root) {
    return {};
  }
  // The Root node returned by the API is just a container for the actual root node. It provides
  // an easy way to find the root of the Project Tree. We currently expect the root node to have
  // exactly one child, which is the actual root of the tree.
  const rootNode = projectTree.nodes.find((node) => node.parentId === root.id);
  if (!rootNode) {
    return {};
  }

  const nodes = [...projectTree.nodes, ...projectChildrenTree.nodes, ...placeholderNodes];

  return {
    root: buildNodeTree(nodes, rootNode, null, undefined, availableUiFeatures)
  };
}

// The SettingsTreeDto contains a flat list of nodes and relationships. We need to build a tree
// structure from this flat list. This function takes the root node of the tree and recursively
// builds the tree structure.
function buildNodeTree(
  nodes: EnrichedSettingsNodeDto[],
  node: EnrichedSettingsNodeDto,
  parentId: string | null,
  cache: SettingsTreeBuilderCache = { nodesVisited: new Set<string>() },
  availableUiFeatures: string[]
): ISettingsNode {
  const hasProjectRelationship = isRelatedToProjectRelationship(node, nodes);

  // Filter relationships to find children of the current node, only if this is the first time we encounter this node.
  const children = hasProjectRelationship
    ? []
    : nodes
        .filter(
          (n) => n.parentId === node.id && isVisibleNode(n, availableUiFeatures) && !isRedundantToProjectNode(node, n)
        )
        .sort(sortSettingsNodes)
        .map((child) => buildNodeTree(nodes, child, node.id, cache, availableUiFeatures));

  // Hide empty folders so the tree does not get cluttered with empty containers. "Managed carriers"
  // is always empty here, because everything it holds is a Group node and those are not rendered.
  const isHidden =
    children.length === 0 &&
    (node.type === KnownSettingsNodeDtoType.Hubs || node.type === KnownSettingsNodeDtoType.GroupsManagedBy);
  // The children of the Project node are fetched when the node is expanded. So it must always be expandable.
  const isExpandableWhenEmpty =
    node.type === KnownSettingsNodeDtoType.Project && !node.isPlaceholder && !hasProjectRelationship;
  return {
    label: node.displayLabel,
    type: node.type as KnownSettingsNodeDtoType,
    subType: node.subType,
    isPlaceholder: node.isPlaceholder,
    id: node.id,
    parentId: parentId,
    children,
    isHidden,
    isExpandableWhenEmpty,
    status: node.status as KnownSettingsNodeDtoStatus,
    style: node.style,
    requestType: node.request.type
  };
}

function isVisibleNode(node: EnrichedSettingsNodeDto, availableUiFeatures: string[]): boolean {
  switch (node.type) {
    case KnownSettingsNodeDtoType.Group:
    case KnownSettingsNodeDtoType.GroupsManagedBy:
    case KnownSettingsNodeDtoType.HubAllocations:
    case KnownSettingsNodeDtoType.Tower:
    case KnownSettingsNodeDtoType.Locker:
    case KnownSettingsNodeDtoType.Hub:
    case KnownSettingsNodeDtoType.Hubs:
    case KnownSettingsNodeDtoType.HubLayout:
    case KnownSettingsNodeDtoType.Kiosk:
    case KnownSettingsNodeDtoType.KioskTheme:
    case KnownSettingsNodeDtoType.ProjectModulesFolder:
    case KnownSettingsNodeDtoType.ProjectModule:
      return availableUiFeatures.includes('internal-groundfloor');
    default:
      return true;
  }
}

function isRedundantToProjectNode(parentNode: EnrichedSettingsNodeDto, childNode: EnrichedSettingsNodeDto): boolean {
  // Currently used to hide the current root project entry in "Available to",
  // but intentionally named generically so it can evolve for other
  // project-context redundancy rules.
  if (parentNode.type !== KnownSettingsNodeDtoType.Hubs) {
    return false;
  }

  if (!isAvailableToFolder(parentNode)) {
    return false;
  }

  if (childNode.type !== KnownSettingsNodeDtoType.Project) {
    return false;
  }

  return childNode.entityId === childNode.request.rootId;
}

function isRelatedToProjectRelationship(node: EnrichedSettingsNodeDto, nodes: EnrichedSettingsNodeDto[]): boolean {
  if (node.type !== KnownSettingsNodeDtoType.Project || !node.parentId) {
    return false;
  }

  const parentNode = nodes.find((n) => n.id === node.parentId);
  if (!parentNode) {
    return false;
  }

  return isAvailableToFolder(parentNode) || isAccessedVia(parentNode);
}

function isAvailableToFolder(node: EnrichedSettingsNodeDto): boolean {
  if (node.type !== KnownSettingsNodeDtoType.ProjectLinkFolder) {
    return false;
  }

  return node.entityId.endsWith('-available-to') || node.displayLabel.startsWith('Available to');
}

function isAccessedVia(node: EnrichedSettingsNodeDto): boolean {
  if (node.type !== KnownSettingsNodeDtoType.ProjectLinkFolder) {
    return false;
  }

  return node.entityId.endsWith('-accessed-via') || node.displayLabel.startsWith('Accessed via');
}

function sortSettingsNodes(a: EnrichedSettingsNodeDto, b: EnrichedSettingsNodeDto): number {
  // Placeholders should be at the start of the list
  if (a.isPlaceholder) {
    return -1;
  }

  if (b.isPlaceholder) {
    return 1;
  }

  // Define type priority order
  const getTypePriority = (type: KnownSettingsNodeDtoType): number => {
    switch (type) {
      case KnownSettingsNodeDtoType.Group:
        return 0;
      case KnownSettingsNodeDtoType.GroupsManagedBy:
        return 0;
      case KnownSettingsNodeDtoType.Project:
        return 1;
      case KnownSettingsNodeDtoType.AccessControl:
        return 2;
      case KnownSettingsNodeDtoType.ProjectModulesFolder:
        return 3;
      case KnownSettingsNodeDtoType.ProjectModule:
        return 4;
      case KnownSettingsNodeDtoType.Hub:
        return 5;
      case KnownSettingsNodeDtoType.HubLayout:
        return 6;
      case KnownSettingsNodeDtoType.Kiosk:
        return 7;
      case KnownSettingsNodeDtoType.HubAllocations:
        return 8;
      case KnownSettingsNodeDtoType.KioskTheme:
        return 9;
      case KnownSettingsNodeDtoType.Tower:
        return 10;
      case KnownSettingsNodeDtoType.Locker:
        return 11;
      case KnownSettingsNodeDtoType.RegistrationPage:
        return 12;
      default:
        return 999; // All other types at the end
    }
  };

  // Sort by type priority first
  const aPriority = getTypePriority(a.type as KnownSettingsNodeDtoType);
  const bPriority = getTypePriority(b.type as KnownSettingsNodeDtoType);

  if (aPriority !== bPriority) {
    return aPriority - bPriority;
  }

  // Within the same type if we have an optional order index, order by that
  if (a.orderIndex != null && b.orderIndex != null && a.orderIndex !== b.orderIndex) {
    return a.orderIndex - b.orderIndex;
  }

  // Within the same type, sort by display label
  return a.displayLabel.localeCompare(b.displayLabel);
}
